import React from "react";
import { Briefcase, MapPin, Clock, Mail, Send } from "lucide-react";

const LANGUAGES = [
  "Punjabi",
  "Bengali",
  "Telugu",
  "Tamil",
  "Gujarati",
  "Chinese",
  "Ukrainian",
];

const PERKS = [
  { icon: MapPin, label: "Edmonton, Calgary & surrounding Alberta" },
  { icon: Clock, label: "Full-time, part-time, casual shifts" },
  { icon: Briefcase, label: "Training & ongoing support provided" },
];

export const Careers = () => {
  return (
    <section
      id="careers"
      data-testid="careers-section"
      className="section-padding bg-white"
    >
      <div className="container-x">
        <div className="max-w-3xl">
          <div className="eyebrow">Careers at Aegis Care</div>
          <h2
            data-testid="careers-heading"
            className="font-display text-3xl md:text-5xl font-semibold mt-3 aegis-navy-text"
          >
            Join a team that treats every client like family
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            We're growing — and we believe great home care begins with great
            people. If you're compassionate, dependable, and ready to make a
            real difference in an Alberta family's life, we'd love to hear from
            you.
          </p>
        </div>

        <div
          data-testid="careers-job-card"
          className="mt-12 rounded-2xl border border-gray-200 bg-[#F4F7F6] p-8 md:p-10"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <span
                data-testid="careers-job-badge"
                className="inline-block px-3 py-1 rounded-full bg-[#E3EEEC] text-[#2A5C56] text-xs uppercase tracking-widest font-semibold"
              >
                Now hiring
              </span>
              <h3 className="font-display text-2xl md:text-3xl font-semibold mt-3 aegis-navy-text">
                Multilingual Home Care Aides &amp; Companions
              </h3>
            </div>
            <a
              href="mailto:hr@aegishomecare.ca?subject=Application%20-%20Aegis%20Care%20Caregiver"
              data-testid="careers-apply-cta"
              className="btn-primary"
            >
              <Send size={16} />
              Apply Now
            </a>
          </div>

          <p className="mt-6 text-gray-700 leading-relaxed">
            We're actively looking for <strong>passionate caregivers and
            home-care professionals</strong> who speak one or more of the
            following languages — so we can match every client with someone who
            truly understands them:
          </p>

          <div
            data-testid="careers-languages"
            className="mt-5 flex flex-wrap gap-2"
          >
            {LANGUAGES.map((l) => (
              <span
                key={l}
                data-testid={`careers-lang-${l.toLowerCase()}`}
                className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium aegis-navy-text"
              >
                {l}
              </span>
            ))}
          </div>

          <div
            data-testid="careers-perks"
            className="mt-8 grid sm:grid-cols-3 gap-4"
          >
            {PERKS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.label}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <Icon
                    size={20}
                    className="text-[#3D7A73] flex-shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <span className="text-sm text-gray-700">{p.label}</span>
                </div>
              );
            })}
          </div>

          <div
            data-testid="careers-apply-block"
            className="mt-8 p-5 rounded-xl aegis-navy-bg text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              <Mail size={20} className="text-[#9BD3CB]" aria-hidden="true" />
              <div>
                <div className="text-xs uppercase tracking-widest text-white/60 font-semibold">
                  To apply, send your resume to
                </div>
                <a
                  href="mailto:hr@aegishomecare.ca?subject=Application%20-%20Aegis%20Care%20Caregiver"
                  data-testid="careers-email-link"
                  className="font-display font-semibold text-lg hover:text-[#9BD3CB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#9BD3CB] rounded"
                >
                  hr@aegishomecare.ca
                </a>
              </div>
            </div>
            <a
              href="mailto:hr@aegishomecare.ca?subject=Application%20-%20Aegis%20Care%20Caregiver"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#3D7A73] hover:bg-[#2A5C56] text-white text-sm font-medium transition-colors"
            >
              <Send size={14} />
              Email Application
            </a>
          </div>

          <p className="mt-6 text-xs text-gray-500">
            Aegis Care Operations Ltd. is an equal-opportunity employer. We
            welcome applications from all qualified individuals regardless of
            background.
          </p>
        </div>
      </div>
    </section>
  );
};
